import React, { useEffect, useState, useRef } from 'react';
import { Card, CardBody, CardHeader, Col, Container, Row, Spinner } from 'reactstrap';
import JobCardGrid from './JobCardGrid';
import { useUser } from '../../hooks';
import firebase from 'firebase/compat/app';
import 'firebase/compat/firestore';
import { Job } from '../../models/Job.interface';
import { useFirebaseDedup } from '../../hooks/useFirebaseDedup';
import BreadCrumb from '../../Components/Common/BreadCrumb';

interface MatchedJob extends Job {
    matchedAt: Date;
    hasViewed: boolean;
    hasBid: boolean;
}

const ContractorDashboard = () => {
    const [matchedJobs, setMatchedJobs] = useState<MatchedJob[]>([]);
    const [loading, setLoading] = useState(true);
    const { currentUserId, contractor } = useUser();
    const { dedupRequest } = useFirebaseDedup();
    const isMountedRef = useRef(true);

    // Helper function to safely convert dates
    const safeDateConversion = (dateField: any): Date | undefined => {
        try {
            if (!dateField) return undefined;
            
            // If it's already a Date object
            if (dateField instanceof Date) {
                return dateField;
            }
            
            // If it's a Firestore Timestamp
            if (dateField && typeof dateField === 'object' && 'toDate' in dateField) {
                return dateField.toDate();
            }
            
            // If it's a string or number, try to create a Date
            if (typeof dateField === 'string' || typeof dateField === 'number') {
                const date = new Date(dateField);
                return isNaN(date.getTime()) ? undefined : date;
            }
            
            return undefined;
        } catch (error) {
            console.error('Error converting date:', error, dateField);
            return undefined;
        }
    };

    // Memoize the fetchMatchedJobs function
    const fetchMatchedJobs = React.useCallback(async () => {
        if (!currentUserId) return;

        const firestore = firebase.firestore();
        setLoading(true);

        console.log("contractor?.contractorId", contractor?.contractorId);

        const contractorId = contractor?.contractorId || currentUserId;
        try {
            const matchedJobsRef = firestore.collection('jobContractorMatches')
                .where('contractorId', '==', contractorId)
                .orderBy('matchedAt', 'desc');

            const matchedJobsSnapshot = await dedupRequest(
                `contractor-matches-${contractorId}`,
                () => matchedJobsRef.get()
            );

            console.log('Got matches, fetching job details...');

            const jobPromises = matchedJobsSnapshot.docs.map(async (doc) => {
                const matchData = doc.data();
                const jobId = matchData.jobId; // Extract jobId from match data

                if (!jobId) {
                    console.log('No jobId found in match data');
                    return null;
                }

                try {
                    // Fetch job details using jobId from matchData
                    const jobDoc = await dedupRequest(
                        `job-${jobId}`,
                        () => firestore.collection('jobs').doc(jobId).get()
                    );

                    if (!jobDoc.exists) {
                        console.log(`Job ${jobId} not found`);
                        return null;
                    }

                    const jobData = jobDoc.data();

                    if (!jobData) {
                        console.log(`No data for job ${jobId}`);
                        return null;
                    }

                    // Fetch bid count
                    const bidsSnapshot = await dedupRequest(
                        `job-bids-${jobId}`,
                        () => firestore.collection('jobs').doc(jobId).collection('bids').get()
                    );

                    // Safely convert dates
                    const dtCreated = safeDateConversion(jobData.dtCreated);
                    const matchedAt = safeDateConversion(matchData.matchedAt);

                    // Create a properly typed MatchedJob object
                    const matchedJob: MatchedJob = {
                        // Required Job fields
                        id: jobId,
                        jobTitle: jobData.jobTitle || '',
                        jobCategories: jobData.jobCategories || [],
                        propertyAddress: jobData.propertyAddress || {
                            address1: '',
                            city: '',
                            state: '',
                            zipCode: ''
                        },
                        jobSummary: jobData.jobSummary || '',
                        jobDescription: jobData.jobDescription || '',
                        jobDetail: jobData.jobDetail || '',
                        userId: jobData.userId || '',
                        dtCreated: dtCreated ? dtCreated.toISOString() : new Date().toISOString(),
                        jobAttachments: jobData.jobAttachments || [],
                        seoUrl: jobData.seoUrl || '',
                        jobStatus: jobData.jobStatus || '',
                        complete: jobData.complete || false,
                        
                        // MatchedJob specific fields
                        matchedAt: matchedAt || new Date(),
                        hasViewed: matchData.hasViewed || false,
                        hasBid: matchData.hasBid || false,
                        numBids: bidsSnapshot.size,
                        
                        // Optional fields that might exist
                        contractId: jobData.contractId,
                    };

                    return matchedJob;
                } catch (jobError) {
                    console.error(`Error processing job ${jobId}:`, jobError);
                    return null;
                }
            });

            console.log('Waiting for all job details...');
            const jobResults = await Promise.all(jobPromises);
            console.log('Got all job details');

            if (isMountedRef.current) {
                const validJobs = jobResults.filter((job): job is MatchedJob => job !== null);
                console.log(`Setting ${validJobs.length} matched jobs`);
                setMatchedJobs(validJobs);
                setLoading(false);
            }
        } catch (error) {
            console.error('Error fetching matched jobs:', error);
            if (isMountedRef.current) {
                setLoading(false);
            }
        }
    }, [currentUserId, contractor, dedupRequest]);

    useEffect(() => {
        isMountedRef.current = true;
        fetchMatchedJobs();

        return () => {
            isMountedRef.current = false;
        };
    }, [fetchMatchedJobs]);

    // Update view status when a job is viewed
    const handleCardClick = async (jobId: string) => {
        if (!currentUserId) return;

        const firestore = firebase.firestore();
        const batch = firestore.batch();
        const contractorId = contractor?.contractorId || currentUserId;

        try {
            // Update in contractor's matched-jobs subcollection
            const contractorJobRef = firestore.collection('contractors')
                .doc(contractorId)
                .collection('matched-jobs')
                .doc(jobId);

            batch.update(contractorJobRef, {
                hasViewed: true,
                lastViewedAt: firebase.firestore.FieldValue.serverTimestamp()
            });

            // Update in jobContractorMatches collection
            const matchQuery = await firestore.collection('jobContractorMatches')
                .where('jobId', '==', jobId)
                .where('contractorId', '==', contractorId)
                .limit(1)
                .get();

            if (!matchQuery.empty) {
                batch.update(matchQuery.docs[0].ref, {
                    hasViewed: true,
                    lastViewedAt: firebase.firestore.FieldValue.serverTimestamp()
                });
            }

            await batch.commit();

            // Update local state
            setMatchedJobs(prevJobs =>
                prevJobs.map(job =>
                    job.id === jobId
                        ? { ...job, hasViewed: true }
                        : job
                )
            );
        } catch (error) {
            console.error('Error updating view status:', error);
        }
    };

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <BreadCrumb title="Projects" pageTitle="Dashboards" />
                    <Row>
                        <Col lg={12}>
                            <Card>
                                <CardHeader>
                                    <h4 className="card-title mb-0">{contractor?.businessName}</h4>
                                </CardHeader>
                                <CardBody>
                                    {loading ? (
                                        <div className="text-center my-5">
                                            <Spinner color="primary" />
                                        </div>
                                    ) : (
                                        <JobCardGrid
                                            jobs={matchedJobs}
                                            onDeleteJob={() => { }}  // Contractors can't delete jobs
                                            isLoading={false}
                                            onCardClick={handleCardClick}
                                        />
                                    )}
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </div>
        </React.Fragment>
    );
};

export default ContractorDashboard;